import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { collection, getDocs, query, where, doc, setDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import OpportunityCard from "../components/OpportunityCard";
import OpportunityTabs from "../components/OpportunityTabs";
import LoadingSpinner from "../components/LoadingSpinner";
import EmptyState from "../components/EmptyState";

const Explore = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [internships, setInternships] = useState([]);
  const [hackathons, setHackathons] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get("search") || "");
  const [savedInternships, setSavedInternships] = useState({});

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [internRes, hackRes, placeRes] = await Promise.all([
          getDocs(collection(db, "internships")),
          getDocs(collection(db, "hackathons")),
          getDocs(collection(db, "placements")),
        ]);

        setInternships(internRes.docs.map((d) => ({ id: d.id, ...d.data(), _type: "internship" })));
        setHackathons(hackRes.docs.map((d) => ({ id: d.id, ...d.data(), _type: "hackathon" })));
        setPlacements(placeRes.docs.map((d) => ({ id: d.id, ...d.data(), _type: "placement" })));
      } catch (error) {
        console.error("Error fetching opportunities:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  useEffect(() => {
    if (!user) return;
    const fetchSaved = async () => {
      const q = query(collection(db, "bookmarks"), where("userId", "==", user.uid));
      const snap = await getDocs(q);
      const saved = {};
      snap.forEach((d) => { saved[d.data().internshipId] = true; });
      setSavedInternships(saved);
    };
    fetchSaved();
  }, [user]);

  const toggleBookmark = async (id) => {
    if (!user) {
      alert("Please sign in to save opportunities.");
      return;
    }
    const ref = doc(db, "bookmarks", `${user.uid}_${id}`);
    if (savedInternships[id]) {
      await deleteDoc(ref);
      setSavedInternships((prev) => ({ ...prev, [id]: false }));
    } else {
      await setDoc(ref, { userId: user.uid, internshipId: id });
      setSavedInternships((prev) => ({ ...prev, [id]: true }));
    }
  };

  const allOpportunities = [...internships, ...hackathons, ...placements];

  const getFiltered = () => {
    let items;
    switch (activeTab) {
      case "internships": items = internships; break;
      case "hackathons": items = hackathons; break;
      case "placements": items = placements; break;
      default: items = allOpportunities;
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (item) =>
          item.title?.toLowerCase().includes(q) ||
          item.company?.toLowerCase().includes(q) ||
          item.organizer?.toLowerCase().includes(q) ||
          item.location?.toLowerCase().includes(q)
      );
    }

    return items;
  };

  const filtered = getFiltered();

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <LoadingSpinner text="Loading opportunities..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="page-container">
        <div className="mb-8">
          <h1 className="section-title">Explore Opportunities</h1>
          <p className="text-gray-500 mt-2">
            Browse all available internships, hackathons, and placements in one place.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <OpportunityTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            counts={{
              all: allOpportunities.length,
              internships: internships.length,
              hackathons: hackathons.length,
              placements: placements.length,
            }}
          />
        </div>

        <div className="mb-6">
          <div className="relative">
            <svg
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search by title, company, or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-field pl-11"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            title="No opportunities found"
            message="Try changing your search query or selecting a different category."
          />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 animate-fade-in">
            {filtered.map((item) => (
              <OpportunityCard
                key={item.id}
                opportunity={item}
                type={item._type}
                isSaved={!!savedInternships[item.id]}
                onToggleBookmark={toggleBookmark}
                showBookmark={item._type !== "hackathon"}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Explore;
